import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { createFilterQuery } from "@via/pages/account/profile/integration-tokens/utils/helper";
import { useNotifications } from "@via/hooks/useNotifications";
import { ErrorNotification } from "@via/utils/interfaces";
import {
  CreateAIInstructionRequestBody,
  UpdateAIInstructionRequestBody,
  UpdateAIInstructionQueryParams,
  ListServiceTokensResponse,
} from "common";

export const fetchCustomInstructions = async (
  take: number,
  skip: number,
  filter?: Array<{ id: string; value: unknown }>
): Promise<ListServiceTokensResponse> => {
  try {
    const baseUrl = "/api/user/v1/ai-instruction";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });
    if (filter && filter.length > 0) {
      const queryFilter = createFilterQuery(filter);
      queryParams.append("filter", queryFilter);
    }
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useCustomInstructions = (
  page: number = 0,
  pageSize: number = 10,
  filters: Array<{ id: string; value: unknown }> = []
) => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/user/v1/ai-instruction", take, skip, filters],
    queryFn: () => fetchCustomInstructions(take, skip, filters),
    retry: false,
  });
};

export const useCreateCustomInstruction = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: CreateAIInstructionRequestBody) => {
      const res = await axios.post("/api/user/v1/ai-instruction", payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "custom-instruction-created",
        variant: NOTIFICATION.SUCCESS,
        message: "Custom instruction created successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/ai-instruction"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "custom-instruction-creation-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to create custom instruction",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useDeleteCustomInstruction = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete(`/api/user/v1/ai-instruction/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/ai-instruction"],
      });
      setNotification({
        id: "custom-instruction-deleted-successfully",
        variant: NOTIFICATION.SUCCESS,
        message: "Custom instruction deleted successfully",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "custom-instruction-deletion-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to delete custom instruction",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useUpdateCustomInstruction = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (
      payload: UpdateAIInstructionRequestBody & UpdateAIInstructionQueryParams
    ) => {
      const res = await axios.put(`/api/user/v1/ai-instruction/${payload.id}`, {
        name: payload.name,
        instruction: payload.instruction,
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "custom-instruction-updated",
        variant: NOTIFICATION.SUCCESS,
        message: "Custom instruction updated successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/ai-instruction"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "custom-instruction-update-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to update custom instruction",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};
